import React, { useCallback, useEffect, useMemo, useState, useRef, useId, createElement } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "../context/TranslationContext";
import { DraftColumn } from "../components/DraftColumn";
import { MergeEditor } from "../components/MergeEditor";
import { GlossaryDrawer } from "../components/GlossaryDrawer";
import { AddToGlossaryModal } from "../components/AddToGlossaryModal";
import { translateSegment, DEFAULT_MODELS } from "../data/translationService";
import type { CachedSegmentWork } from "../data/serverDraftCache";
import { syncTranslationsToFrTemplate, segmentsToInjectionTexts } from "../data/lexicalParser";
import { ArrowLeft, Book, ChevronLeft, ChevronRight, Wand2, Check, Key, X, Code2, RefreshCw } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
type DraftStatus = "idle" | "loading" | "error" | "success";
export const SegmentWorkspace: React.FC = () => {
  const {
    docId,
    segmentId
  } = useParams<{
    docId: string;
    segmentId: string;
  }>();
  const navigate = useNavigate();
  const {
    segments,
    updateSegmentStatus,
    unlockSegment,
    splitSection,
    documentTitle,
    setCurrentDocumentId,
    glossary,
    openRouterApiKey,
    setOpenRouterApiKey,
    modelConfigs,
    currentDocument,
    getSegmentDraftCache,
    saveSegmentDraftCache,
    clearSegmentDraftCache
  } = useTranslation();
  useEffect(() => {
    if (docId) setCurrentDocumentId(docId);
  }, [docId, setCurrentDocumentId]);
  const currentIndex = segments.findIndex(s => s.id === segmentId);
  const segment = segments[currentIndex];
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);
  const [showGlossaryModal, setShowGlossaryModal] = useState(false);
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [tempApiKey, setTempApiKey] = useState(openRouterApiKey);
  const [showJsonViewer, setShowJsonViewer] = useState(false);
  const previewJson = useMemo(() => {
    if (!showJsonViewer || !currentDocument) return "";
    const translatedTexts = segmentsToInjectionTexts(segments);
    const enBody = currentDocument.enBody;
    const frBody = currentDocument.frBody;
    if (!enBody?.trim() || !frBody?.trim()) return "// No body available";
    const {
      body
    } = syncTranslationsToFrTemplate(enBody, frBody, translatedTexts);
    return body;
  }, [showJsonViewer, currentDocument, segments]);
  const approvedCount = segments.filter(s => s.status === "approved").length;
  const totalCount = segments.length;
  // Draft states
  const [draftStatuses, setDraftStatuses] = useState<Record<"A" | "B" | "C", DraftStatus>>({
    A: "idle",
    B: "idle",
    C: "idle"
  });
  const [draftTexts, setDraftTexts] = useState<Record<"A" | "B" | "C", string>>({
    A: "",
    B: "",
    C: ""
  });
  const [draftErrors, setDraftErrors] = useState<Record<"A" | "B" | "C", string>>({
    A: "",
    B: "",
    C: ""
  });
  const [draftModels, setDraftModels] = useState<Record<"A" | "B" | "C", string>>({
    A: modelConfigs[0]?.label || "Model A",
    B: modelConfigs[1]?.label || "Model B",
    C: modelConfigs[2]?.label || "Model C"
  });
  // Merge Editor state
  const [mergedHtml, setMergedHtml] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const mergedHtmlSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRestoredSegmentRef = useRef<string | null>(null);
  const skipMergedHtmlPersistRef = useRef(false);
  // Reset state when segment changes — restore from cache if available
  useEffect(() => {
    if (!segment || !segmentId) return;
    if (lastRestoredSegmentRef.current === segmentId) return;
    lastRestoredSegmentRef.current = segmentId;
    skipMergedHtmlPersistRef.current = true;
    const cached = getSegmentDraftCache(segmentId);
    if (cached?.drafts) {
      const newStatuses: Record<"A" | "B" | "C", DraftStatus> = {
        A: "idle",
        B: "idle",
        C: "idle"
      };
      const newTexts: Record<"A" | "B" | "C", string> = {
        A: "",
        B: "",
        C: ""
      };
      const newErrors: Record<"A" | "B" | "C", string> = {
        A: "",
        B: "",
        C: ""
      };
      const newModels: Record<"A" | "B" | "C", string> = {
        A: modelConfigs[0]?.label || "Model A",
        B: modelConfigs[1]?.label || "Model B",
        C: modelConfigs[2]?.label || "Model C"
      };
      for (const key of ["A", "B", "C"] as const) {
        const draft = cached.drafts[key];
        if (draft) {
          newStatuses[key] = draft.status;
          newTexts[key] = draft.text || "";
          newErrors[key] = draft.error || "";
          newModels[key] = draft.model || newModels[key];
        }
      }
      setDraftStatuses(newStatuses);
      setDraftTexts(newTexts);
      setDraftErrors(newErrors);
      setDraftModels(newModels);
    } else {
      setDraftStatuses({
        A: "idle",
        B: "idle",
        C: "idle"
      });
      setDraftTexts({
        A: "",
        B: "",
        C: ""
      });
      setDraftErrors({
        A: "",
        B: "",
        C: ""
      });
    }
    // Restore merge editor: prefer approved mergedText, then cached HTML
    if (segment.mergedText) {
      setMergedHtml(`<span>${segment.mergedText}</span>`);
    } else if (cached?.mergedHtml) {
      setMergedHtml(cached.mergedHtml);
    } else {
      setMergedHtml("");
    }
    setHistory([]);
  }, [segmentId, segment, modelConfigs, getSegmentDraftCache]);
  // Debounce-save mergedHtml to server draft cache
  useEffect(() => {
    if (!segmentId) return;
    if (skipMergedHtmlPersistRef.current) {
      skipMergedHtmlPersistRef.current = false;
      return;
    }
    if (!mergedHtml.trim()) return;
    if (mergedHtmlSaveTimer.current) clearTimeout(mergedHtmlSaveTimer.current);
    mergedHtmlSaveTimer.current = setTimeout(() => {
      saveSegmentDraftCache(segmentId, {
        mergedHtml
      });
    }, 500);
    return () => {
      if (mergedHtmlSaveTimer.current) clearTimeout(mergedHtmlSaveTimer.current);
    };
  }, [mergedHtml, segmentId, saveSegmentDraftCache]);
  const generateDrafts = useCallback(async () => {
    if (!segment) return;
    if (!openRouterApiKey) {
      setShowApiKeyInput(true);
      toast.error("Please set your OpenRouter API key first");
      return;
    }
    setDraftStatuses({
      A: "loading",
      B: "loading",
      C: "loading"
    });
    setDraftTexts({
      A: "",
      B: "",
      C: ""
    });
    setDraftErrors({
      A: "",
      B: "",
      C: ""
    });
    if (segment.status === "not_started") {
      updateSegmentStatus(segment.id, "in_progress");
    }
    await translateSegment({
      apiKey: openRouterApiKey,
      sourceText: segment.sourceText,
      segmentId: segment.clauseId,
      glossary,
      documentContext: documentTitle,
      priorApprovedText: segment.mergedText,
      models: modelConfigs,
      onDraftComplete: result => {
        const id = result.draftId;
        if (result.error) {
          setDraftStatuses(prev => ({
            ...prev,
            [id]: "error"
          }));
          setDraftErrors(prev => ({
            ...prev,
            [id]: result.error || "Unknown error"
          }));
          // Cache the error state
          if (segmentId) {
            saveSegmentDraftCache(segmentId, {
              drafts: {
                [id]: {
                  text: "",
                  model: result.modelLabel,
                  status: "error",
                  error: result.error
                }
              }
            });
          }
        } else {
          setDraftStatuses(prev => ({
            ...prev,
            [id]: "success"
          }));
          setDraftTexts(prev => ({
            ...prev,
            [id]: result.text
          }));
          if (segmentId) {
            saveSegmentDraftCache(segmentId, {
              drafts: {
                [id]: {
                  text: result.text,
                  model: result.modelLabel,
                  status: "success"
                }
              }
            });
          }
        }
        setDraftModels(prev => ({
          ...prev,
          [id]: result.modelLabel
        }));
      }
    });
  }, [segment, openRouterApiKey, glossary, documentTitle, modelConfigs]);
  const handleRetry = useCallback(async (draftId: "A" | "B" | "C") => {
    if (!segment || !openRouterApiKey) return;
    const modelConfig = modelConfigs.find(m => m.draftId === draftId);
    if (!modelConfig) return;
    setDraftStatuses(prev => ({
      ...prev,
      [draftId]: "loading"
    }));
    setDraftErrors(prev => ({
      ...prev,
      [draftId]: ""
    }));
    await translateSegment({
      apiKey: openRouterApiKey,
      sourceText: segment.sourceText,
      segmentId: segment.clauseId,
      glossary,
      documentContext: documentTitle,
      priorApprovedText: segment.mergedText,
      models: [modelConfig],
      onDraftComplete: result => {
        if (result.error) {
          setDraftStatuses(prev => ({
            ...prev,
            [draftId]: "error"
          }));
          setDraftErrors(prev => ({
            ...prev,
            [draftId]: result.error || "Unknown error"
          }));
          if (segmentId) {
            saveSegmentDraftCache(segmentId, {
              drafts: {
                [draftId]: {
                  text: "",
                  model: result.modelLabel,
                  status: "error",
                  error: result.error
                }
              }
            });
          }
        } else {
          setDraftStatuses(prev => ({
            ...prev,
            [draftId]: "success"
          }));
          setDraftTexts(prev => ({
            ...prev,
            [draftId]: result.text
          }));
          if (segmentId) {
            saveSegmentDraftCache(segmentId, {
              drafts: {
                [draftId]: {
                  text: result.text,
                  model: result.modelLabel,
                  status: "success"
                }
              }
            });
          }
        }
      }
    });
  }, [segment, openRouterApiKey, glossary, documentTitle, modelConfigs]);
  const handleSelectSentence = (sentence: string, sourceId: "A" | "B" | "C") => {
    setHistory(prev => [...prev, mergedHtml]);
    // Sentences arrive already trimmed from DraftColumn, so when we append one
    // after another we must re-insert the separating space. Previously we used
    // a `<br><br>` separator, but <br> contributes NO character to
    // `.textContent` (what Save + Sync extract), so consecutive picks ran
    // together as "Phrase un.Phrase deux." with no space after the period.
    // Use a real space, and only when the running text doesn't already end in
    // whitespace, so we never double-space.
    const tmp = document.createElement("div");
    tmp.innerHTML = mergedHtml;
    const existingText = tmp.textContent || "";
    const needsSpace = existingText.length > 0 && !/\s$/.test(existingText);
    const separator = needsSpace ? " " : "";
    const newHtml = `${mergedHtml}${separator}<span data-source="${sourceId}">${sentence}</span>`;
    setMergedHtml(newHtml);
  };
  const handleUndo = () => {
    if (history.length > 0) {
      setMergedHtml(history[history.length - 1]);
      setHistory(prev => prev.slice(0, -1));
    }
  };
  const handleClear = () => {
    setHistory(prev => [...prev, mergedHtml]);
    setMergedHtml("");
  };
  const handleSave = (andNext = false) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = mergedHtml;
    const plainText = tempDiv.textContent || tempDiv.innerText || "";
    updateSegmentStatus(segment!.id, "approved", plainText, {
      lock: andNext
    });
    toast.success(andNext ? `Segment ${currentIndex + 1} approved and locked` : `Segment ${currentIndex + 1} approved`);
    if (andNext && currentIndex < segments.length - 1) {
      navigate(`/document/${docId}/translate/${segments[currentIndex + 1].id}`);
    } else if (!andNext) {
      setShowGlossaryModal(true);
    }
  };
  const handleModify = () => {
    if (!segment) return;
    unlockSegment(segment.id);
    toast.info("Section unlocked for editing");
  };
  const handleSplitSection = () => {
    if (!segment) return;
    const midpoint = Math.floor(segment.sourceText.length / 2);
    const splitAt = segment.sourceText.indexOf(" ", midpoint);
    if (splitAt > 0) {
      splitSection(segment.id, splitAt);
      toast.success("Section split into two");
    } else {
      toast.error("Could not find a suitable split point");
    }
  };
  const handleSaveApiKey = () => {
    setOpenRouterApiKey(tempApiKey);
    setShowApiKeyInput(false);
    toast.success("API key saved");
  };
  const handleRetranslate = useCallback(() => {
    if (!segment) return;
    if (segment.locked) {
      const ok = window.confirm("This section is locked. You must click Modify first to unlock it. Unlock and re-translate now?");
      if (!ok) return;
      unlockSegment(segment.id);
    } else if (segment.status === "approved") {
      const ok = window.confirm("This will discard the approved translation for this segment and generate fresh drafts. Continue?");
      if (!ok) return;
    }
    // Reset local merge state
    setMergedHtml("");
    setHistory([]);
    setDraftStatuses({
      A: "idle",
      B: "idle",
      C: "idle"
    });
    setDraftTexts({
      A: "",
      B: "",
      C: ""
    });
    setDraftErrors({
      A: "",
      B: "",
      C: ""
    });
    // Drop any cached drafts / merged HTML for this segment
    if (segmentId) {
      clearSegmentDraftCache(segmentId);
    }
    // Move the segment back to in-progress so progress counts reflect reality
    updateSegmentStatus(segment.id, "in_progress", "");
    // Kick off a fresh round of drafts
    generateDrafts();
  }, [segment, segmentId, generateDrafts, updateSegmentStatus]);
  if (!segment) return <div className="h-screen flex items-center justify-center text-slate-500" data-id="element-547">
        Segment not found
      </div>;
  const wordCount = mergedHtml.replace(/<[^>]*>?/gm, "").trim().split(/\s+/).filter(w => w.length > 0).length;
  const isGenerating = draftStatuses.A === "loading" || draftStatuses.B === "loading" || draftStatuses.C === "loading";
  const hasDrafts = draftStatuses.A === "success" || draftStatuses.B === "success" || draftStatuses.C === "success";
  const allDraftsReady = draftStatuses.A === "success" && draftStatuses.B === "success" && draftStatuses.C === "success";
  return <div className="h-screen flex flex-col bg-slate-100 overflow-hidden" data-id="element-548">
      {/* Top Bar */}
      <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 shrink-0 z-10" data-id="element-549">
        <div className="flex items-center gap-4" data-id="element-550">
          <button onClick={() => navigate(`/document/${docId}`)} className="p-2 -ml-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-551">
            <ArrowLeft className="w-4 h-4" data-id="element-552" />
            Hub
          </button>
          <div className="w-px h-4 bg-slate-200" data-id="element-553" />
          <div className="flex items-center gap-2" data-id="element-554">
            <span className="text-sm font-semibold text-slate-900 truncate max-w-[200px] lg:max-w-[400px]" data-id="element-555">
              {documentTitle}
            </span>
            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium" data-id="element-556">
              Segment {currentIndex + 1} of {segments.length}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-557">
          <button onClick={() => setShowJsonViewer(!showJsonViewer)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${showJsonViewer ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} title="Toggle JSON preview" data-id="element-558">
            <Code2 className="w-3.5 h-3.5" data-id="element-559" />
            JSON
          </button>
          <button onClick={() => {
          setTempApiKey(openRouterApiKey);
          setShowApiKeyInput(true);
        }} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${openRouterApiKey ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-red-50 text-red-700 hover:bg-red-100"}`} data-id="element-560">
            <Key className="w-3.5 h-3.5" data-id="element-561" />
            {openRouterApiKey ? "API Key ✓" : "Set API Key"}
          </button>
          <div className="flex items-center gap-1 mr-1" data-id="element-562">
            <button onClick={() => currentIndex > 0 && navigate(`/document/${docId}/translate/${segments[currentIndex - 1].id}`)} disabled={currentIndex === 0} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-563">
              <ChevronLeft className="w-5 h-5" data-id="element-564" />
            </button>
            <button onClick={() => currentIndex < segments.length - 1 && navigate(`/document/${docId}/translate/${segments[currentIndex + 1].id}`)} disabled={currentIndex === segments.length - 1} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-565">
              <ChevronRight className="w-5 h-5" data-id="element-566" />
            </button>
          </div>
          <button onClick={() => setIsGlossaryOpen(true)} className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-md text-sm font-medium hover:bg-indigo-100 transition-colors" data-id="element-567">
            <Book className="w-4 h-4" data-id="element-568" />
            Glossary
          </button>
        </div>
      </header>

      {/* API Key Modal */}
      {showApiKeyInput && <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" data-id="element-569">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-570">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-571">
              <div className="flex items-center gap-3" data-id="element-572">
                <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-600" data-id="element-573">
                  <Key className="w-5 h-5" data-id="element-574" />
                </div>
                <div data-id="element-575">
                  <h3 className="text-lg font-semibold text-slate-900" data-id="element-576">
                    OpenRouter API Key
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-577">
                    Required to generate AI translations
                  </p>
                </div>
              </div>
              <button onClick={() => setShowApiKeyInput(false)} className="text-slate-400 hover:text-slate-600" data-id="element-578">
                <X className="w-5 h-5" data-id="element-579" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-580">
              <div data-id="element-581">
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-582">
                  API Key
                </label>
                <input type="password" value={tempApiKey} onChange={e => setTempApiKey(e.target.value)} placeholder="sk-or-v1-..." className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500 font-mono" data-id="element-583" />
              </div>
              <p className="text-xs text-slate-400" data-id="element-584">
                Get your key at{" "}
                <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline" data-id="element-585">
                  openrouter.ai/keys
                </a>
                . Stored locally in your browser.
              </p>
              <div className="bg-slate-50 rounded-lg p-3 space-y-1" data-id="element-586">
                <p className="text-xs font-medium text-slate-600" data-id="element-587">
                  Models used:
                </p>
                {modelConfigs.map(m => <div key={m.id} className="text-xs text-slate-500 flex items-center gap-2" data-id="element-588">
                    <span className="font-medium" data-id="element-589">Draft {m.draftId}:</span>{" "}
                    {m.label} <span className="text-slate-400" data-id="element-590">({m.id})</span>
                  </div>)}
              </div>
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3" data-id="element-591">
              <button onClick={() => setShowApiKeyInput(false)} className="flex-1 py-2.5 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-white transition-colors" data-id="element-592">
                Cancel
              </button>
              <button onClick={handleSaveApiKey} disabled={!tempApiKey.trim()} className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors" data-id="element-593">
                Save Key
              </button>
            </div>
          </div>
        </div>}

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden" data-id="element-594">
        {/* Left Panel: Source Text */}
        <div className="w-[30%] min-w-[320px] max-w-[450px] bg-slate-50 border-r border-slate-200 flex flex-col z-0" data-id="element-595">
          <div className="px-6 py-4 border-b border-slate-200 bg-white shadow-sm z-10" data-id="element-596">
            <div className="flex items-start justify-between gap-3" data-id="element-597">
              <div className="min-w-0" data-id="element-598">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-599">
                  Source English
                </div>
                <div className="font-semibold text-slate-800 truncate" data-id="element-600">
                  {segment.clauseId}
                </div>
              </div>
              <button onClick={handleSplitSection} disabled={segment.locked || isGenerating} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200 disabled:opacity-50" title="Split section at midpoint" data-id="element-601">
                Split
              </button>
              {segment.locked ? <button onClick={handleModify} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200" data-id="element-602">
                  Modify
                </button> : <button onClick={handleRetranslate} disabled={isGenerating} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors shrink-0 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${segment.status === "approved" ? "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200" : "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"}`} title="Generate fresh drafts for this segment" data-id="element-603">
                  <RefreshCw className={`w-3.5 h-3.5 ${isGenerating ? "animate-spin" : ""}`} data-id="element-604" />
                  Re-translate
                </button>}
            </div>
          </div>
          <div className="flex-1 p-6 overflow-y-auto font-legal text-[17px] leading-[1.8] text-slate-800" data-id="element-605">
            {segment.sourceText}
          </div>
          {segment.existingTranslation && <div className="border-t border-slate-200 bg-amber-50/50" data-id="element-606">
              <div className="px-6 py-3 border-b border-amber-100/50" data-id="element-607">
                <div className="flex items-center justify-between" data-id="element-608">
                  <div className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-609">
                    Existing FR Translation
                  </div>
                  <button onClick={() => {
                setHistory(prev => [...prev, mergedHtml]);
                setMergedHtml(`<span>${segment.existingTranslation}</span>`);
              }} className="text-[11px] font-medium text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded transition-colors" data-id="element-610">
                    Use as base
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[200px] font-legal text-[15px] leading-[1.7] text-amber-900/80" data-id="element-611">
                {segment.existingTranslation}
              </div>
            </div>}
        </div>

        {/* Right Panel: Drafts & Merge */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-slate-100" data-id="element-612">
          {/* Top Half: Drafts */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden" data-id="element-613">
            {!hasDrafts && !isGenerating ? <div className="flex-1 flex items-center justify-center bg-white" data-id="element-614">
                <div className="text-center max-w-sm" data-id="element-615">
                  <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-616">
                    <Wand2 className="w-8 h-8 text-indigo-600" data-id="element-617" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-618">
                    Generate Translations
                  </h3>
                  <p className="text-slate-500 text-sm mb-2" data-id="element-619">
                    Run the source text through three AI models to compare
                    approaches and terminology.
                  </p>
                  <div className="flex items-center justify-center gap-4 text-xs text-slate-400 mb-6" data-id="element-620">
                    {modelConfigs.map(m => <span key={m.id} className="flex items-center gap-1" data-id="element-621">
                        <span className="font-semibold text-slate-500" data-id="element-622">
                          {m.draftId}
                        </span>{" "}
                        {m.label}
                      </span>)}
                  </div>
                  <button onClick={generateDrafts} className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-623">
                    Generate 3 Drafts
                  </button>
                  {!openRouterApiKey && <p className="text-xs text-amber-600 mt-3" data-id="element-624">
                      ⚠ Set your OpenRouter API key first
                    </p>}
                </div>
              </div> : <div className="flex-1 flex flex-col overflow-hidden" data-id="element-625">
                {allDraftsReady && <div className="bg-white border-b border-slate-200 px-4 py-1.5 flex items-center justify-center shrink-0 shadow-sm z-10" data-id="element-626">
                    <div className="flex items-center gap-2 text-xs text-slate-500 font-medium" data-id="element-627">
                      <div className="flex gap-1" data-id="element-628">
                        <div className="w-2 h-2 rounded-sm bg-blue-200" data-id="element-629" />
                        <div className="w-2 h-2 rounded-sm bg-violet-200" data-id="element-630" />
                        <div className="w-2 h-2 rounded-sm bg-teal-200" data-id="element-631" />
                      </div>
                      Highlighted words differ between drafts
                    </div>
                  </div>}
                <div className="flex-1 grid grid-cols-3 overflow-hidden" data-id="element-632">
                  <DraftColumn sourceId="A" status={draftStatuses.A} text={draftTexts.A} modelLabel={draftModels.A} errorMessage={draftErrors.A} onSelectSentence={handleSelectSentence} onRetry={() => handleRetry("A")} allDraftsReady={allDraftsReady} otherTexts={[draftTexts.B, draftTexts.C]} data-id="element-633" />

                  <DraftColumn sourceId="B" status={draftStatuses.B} text={draftTexts.B} modelLabel={draftModels.B} errorMessage={draftErrors.B} onSelectSentence={handleSelectSentence} onRetry={() => handleRetry("B")} allDraftsReady={allDraftsReady} otherTexts={[draftTexts.A, draftTexts.C]} data-id="element-634" />

                  <DraftColumn sourceId="C" status={draftStatuses.C} text={draftTexts.C} modelLabel={draftModels.C} errorMessage={draftErrors.C} onSelectSentence={handleSelectSentence} onRetry={() => handleRetry("C")} allDraftsReady={allDraftsReady} otherTexts={[draftTexts.A, draftTexts.B]} data-id="element-635" />
                </div>
              </div>}
          </div>

          {/* Bottom Half: Merge Editor */}
          <div className="h-[40%] min-h-[250px] flex flex-col shadow-[0_-4px_20px_rgba(0,0,0,0.05)] z-10" data-id="element-636">
            <MergeEditor htmlContent={mergedHtml} onChange={setMergedHtml} onClear={handleClear} onUndo={handleUndo} wordCount={wordCount} data-id="element-637" />

            {/* Action Bar */}
            <div className="h-16 bg-white border-t border-slate-200 flex items-center justify-between px-6 shrink-0 sticky bottom-0 z-20" data-id="element-638">
              <div className="text-sm text-slate-500" data-id="element-639">
                {segment.locked && segment.status === "approved" && <span className="flex items-center gap-1.5 text-emerald-600 font-medium" data-id="element-640">
                    <Check className="w-4 h-4" data-id="element-641" /> Approved · Locked
                  </span>}
                {!segment.locked && segment.status === "approved" && <span className="flex items-center gap-1.5 text-emerald-600 font-medium" data-id="element-642">
                    <Check className="w-4 h-4" data-id="element-643" /> Approved
                  </span>}
              </div>
              <div className="flex items-center gap-3" data-id="element-644">
                <button onClick={() => handleSave(false)} disabled={wordCount === 0 || segment.locked} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors" data-id="element-645">
                  Save Approved
                </button>
                <button onClick={() => handleSave(true)} disabled={wordCount === 0} className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm" data-id="element-646">
                  Save & Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <GlossaryDrawer isOpen={isGlossaryOpen} onClose={() => setIsGlossaryOpen(false)} data-id="element-647" />

      <AddToGlossaryModal isOpen={showGlossaryModal} onClose={() => setShowGlossaryModal(false)} suggestedEn="pricing" suggestedFr="tarification" data-id="element-648" />

      {/* JSON Viewer Panel */}
      <AnimatePresence data-id="element-649">
        {showJsonViewer && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowJsonViewer(false)} className="fixed inset-0 bg-slate-900/20 z-40" data-id="element-650" />

            <motion.div initial={{
          x: "100%"
        }} animate={{
          x: 0
        }} exit={{
          x: "100%"
        }} transition={{
          type: "spring",
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 h-full w-[600px] bg-slate-900 shadow-2xl z-50 flex flex-col" data-id="element-651">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700" data-id="element-652">
                <div data-id="element-653">
                  <h2 className="text-sm font-semibold text-white flex items-center gap-2" data-id="element-654">
                    <Code2 className="w-4 h-4 text-slate-400" data-id="element-655" />
                    JSON Preview
                  </h2>
                  <p className="text-xs text-slate-400 mt-0.5" data-id="element-656">
                    Live view of translated body — approved segments are
                    injected
                  </p>
                </div>
                <button onClick={() => setShowJsonViewer(false)} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors" data-id="element-657">
                  <X className="w-4 h-4" data-id="element-658" />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4" data-id="element-659">
                <pre className="text-[12px] leading-[1.6] text-emerald-300 font-mono whitespace-pre-wrap break-words" data-id="element-660">
                  {previewJson}
                </pre>
              </div>
              <div className="px-5 py-3 border-t border-slate-700 text-xs text-slate-500" data-id="element-661">
                {currentDocument?.frTemplateId ? `Target: FR template #${currentDocument.frTemplateId}` : "No paired FR template"}
                {" · "}
                {approvedCount}/{totalCount} segments translated
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
};